/**
 * Geometry helpers for slice-to-world coordinate transforms in the legacy
 * (pre-sform) NIfTI loader path.
 */

/** vtkImageData direction column-major: 9 floats, columns are
 *  [rowCosines, columnCosines, sliceNormal]. The third column (indices 6..8)
 *  is the unit slice-normal vector.
 *
 *  Accepts any indexable numeric collection of length 9 — vtk.js typically
 *  returns a `Float64Array` from `getDirection()`, which is not an `Array`
 *  but is index-readable and has a `length`. */
export type Direction9 = ArrayLike<number>;

/**
 * Project the IPP delta from the currently-displayed slice to a target
 * slice onto the slice normal, keeping only the perpendicular (slice-
 * stacking) component.
 *
 * Why: the legacy loader path adds this offset to `vtkImageData.indexToWorld
 * ([c, r, 0])` to shift contours to the target slice's plane. Adding the
 * full IPP delta unconditionally double-counts axes already encoded by
 * (c, r) on cor/sag MPR series. Projecting onto the slice normal collapses
 * to `dz` for pure axial and to ~0 for in-plane components on cor/sag, so
 * contours land at the saved anatomical position regardless of orientation.
 *
 * Assumes the slice normal (third column of `direction`) is unit-length —
 * that's cornerstone's convention. `direction` must be exactly 9 numbers.
 *
 * Returns the projected delta as `[ox, oy, oz]`.
 */
export function projectIppDeltaOntoNormal(
  delta: readonly [number, number, number],
  direction: Direction9,
): [number, number, number] {
  if (direction.length !== 9) {
    throw new Error(
      `projectIppDeltaOntoNormal: direction must have 9 entries, got ${direction.length}`,
    );
  }
  const nx = direction[6];
  const ny = direction[7];
  const nz = direction[8];
  if (
    !Number.isFinite(nx) ||
    !Number.isFinite(ny) ||
    !Number.isFinite(nz) ||
    nx * nx + ny * ny + nz * nz < 1e-6
  ) {
    throw new Error("projectIppDeltaOntoNormal: invalid slice normal in direction matrix");
  }
  const proj = delta[0] * nx + delta[1] * ny + delta[2] * nz;
  return [nx * proj, ny * proj, nz * proj];
}
